import { ChoiceModel } from "../ChoiceModel";
import { removeHtmlTags } from "../../utils";
import { translate } from "./translationUtilities";
import { CARROUSEL_SCALE_HORIZONTAL_WRAP_BUFFER_PX } from "./carrouselUtilities";

/** Consistent height for all horizontal carousel scale cells on desktop. */
export const CAROUSEL_SCALE_CELL_HEIGHT_PX = 80;

/** Responsive cell height for tablet screens (≤768px). */
export const CAROUSEL_SCALE_CELL_HEIGHT_TABLET_PX = 54;

/** Responsive cell height for mobile screens (≤480px). */
export const CAROUSEL_SCALE_CELL_HEIGHT_MOBILE_PX = 48;

/** Legacy reference width; horizontal cells stretch equally across the row. */
export const CAROUSEL_SCALE_CELL_MAX_WIDTH_PX = 80;

/** Minimum cell width at the start of the fluid desktop range (766px viewport). */
export const CAROUSEL_SCALE_FLUID_MIN_WIDTH_LOWER_PX = 54;

/** Minimum cell width at the end of the fluid desktop range (1168px viewport). */
export const CAROUSEL_SCALE_FLUID_MIN_WIDTH_UPPER_PX = 66;

/** Viewport at which fluid desktop min-width interpolation begins. */
export const CAROUSEL_SCALE_FLUID_WIDTH_VIEWPORT_MIN_PX = 766;

/** Viewport at which fluid desktop min-width interpolation ends. */
export const CAROUSEL_SCALE_FLUID_WIDTH_VIEWPORT_MAX_PX = 1168;

/** Minimum cell width on large displays (>1168px) — NPS-style floor; grows with content. */
export const CAROUSEL_SCALE_CELL_MIN_WIDTH_LARGE_PX = 80;

/** Viewport width above which large-display min cell width applies. */
export const CAROUSEL_SCALE_LARGE_DISPLAY_MIN_VIEWPORT_PX = 1169;

/** Minimum cell width on mobile — WCAG minimum tap target. */
export const CAROUSEL_SCALE_CELL_MIN_WIDTH_MOBILE_PX = 44;

/** Horizontal padding steps — try larger padding first, reduce before vertical layout. */
export const CAROUSEL_SCALE_CONTENT_PADDING_X_STEPS = [8, 6, 4, 2] as const;

/** Tighter horizontal padding steps on mobile. */
export const CAROUSEL_SCALE_CONTENT_PADDING_X_STEPS_MOBILE = [4, 2] as const;

/** Matches `.carrousel-scale__item { margin: 3 }` — horizontal margin per item. */
export const CAROUSEL_SCALE_ITEM_MARGIN_PX = 6;

/** When content row width is below this fraction of the container, center content-sized cells. */
export const CAROUSEL_SCALE_CENTER_ROW_FILL_RATIO = 0.85;

export interface CarrouselScaleItemLayout {
    cellWidth: number;
}

export interface CarrouselScaleLayoutPlan {
    useVertical: boolean;
    contentPaddingX: number;
    cellWidths: number[];
    cellHeight: number;
    /** Resolved min cell width used for this layout (fluid 58–68px on desktop). */
    minCellWidthPx: number;
    /** When true, choices use equal flex growth to fill the full row width (NPS-style). */
    stretchToRow: boolean;
    /** When true, content-sized choices and anchors are centered as a group in the row. */
    centerRow: boolean;
}

export interface ComputeCarrouselScaleLayoutPlanParams {
    labels: string[];
    containerWidth: number;
    borderWidthPx: number;
    font: string;
    isMobile?: boolean;
    isTablet?: boolean;
    isLargeDisplay?: boolean;
    itemMarginPx?: number;
    containerBufferPx?: number;
    gapPx?: number;
}

const AVG_CHAR_WIDTH_RATIO = 0.55;

/** Canvas-based text measurement with char-width fallback for environments without canvas. */
export function measureTextWidth(text: string, font: string): number {
    if (!text) {
        return 0;
    }

    if (typeof document !== "undefined") {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (context) {
            context.font = font;
            return context.measureText(text).width;
        }
    }

    const fontSizeMatch = /(\d+(?:\.\d+)?)px/.exec(font);
    const fontSize = fontSizeMatch ? Number.parseFloat(fontSizeMatch[1]) : 16;
    return text.length * fontSize * AVG_CHAR_WIDTH_RATIO;
}

export function getChoiceLabelPlainText(choice: ChoiceModel, language: string): string {
    const text = translate(choice, "Text", language) || "";
    return removeHtmlTags(text);
}

function computeContentCellWidth(
    label: string,
    paddingX: number,
    borderWidthPx: number,
    font: string,
    minCellWidthPx: number,
): number {
    const textWidth = measureTextWidth(label, font);
    const contentWidth = textWidth + paddingX * 2 + borderWidthPx * 2;
    return Math.max(contentWidth, minCellWidthPx);
}

function computeEqualCellWidth(
    containerWidth: number,
    choiceCount: number,
    itemMarginPx: number,
    containerBufferPx: number,
): number {
    const availableWidth = containerWidth - containerBufferPx - choiceCount * itemMarginPx;
    return availableWidth / choiceCount;
}

function totalRowWidth(
    cellWidths: number[],
    itemMarginPx: number,
    containerBufferPx: number,
    gapPx = 0,
): number {
    const totalCellWidth = cellWidths.reduce((sum, width) => sum + width, 0);
    const totalMargin = cellWidths.length * itemMarginPx;
    const totalGap = gapPx * Math.max(0, cellWidths.length - 1);
    return totalCellWidth + totalMargin + totalGap + containerBufferPx;
}

function horizontalRowFits(
    cellWidths: number[],
    containerWidth: number,
    itemMarginPx: number,
    containerBufferPx: number,
    gapPx = 0,
): boolean {
    if (containerWidth <= 0 || cellWidths.length === 0) {
        return false;
    }

    return totalRowWidth(cellWidths, itemMarginPx, containerBufferPx, gapPx) <= containerWidth;
}

function labelFitsInCell(
    label: string,
    cellWidth: number,
    paddingX: number,
    borderWidthPx: number,
    font: string,
): boolean {
    const textWidth = measureTextWidth(label, font);
    return textWidth + paddingX * 2 + borderWidthPx * 2 <= cellWidth;
}

/**
 * Linearly scales min cell width across the fluid desktop range (766px–1168px).
 * Uses the choices container width — not the full viewport — so carousel statement columns
 * and other layout offsets are reflected in the calculation.
 */
export function computeFluidDesktopMinCellWidth(referenceWidth: number): number {
    const widthSpan =
        CAROUSEL_SCALE_FLUID_WIDTH_VIEWPORT_MAX_PX - CAROUSEL_SCALE_FLUID_WIDTH_VIEWPORT_MIN_PX;
    const minWidthSpan =
        CAROUSEL_SCALE_FLUID_MIN_WIDTH_UPPER_PX - CAROUSEL_SCALE_FLUID_MIN_WIDTH_LOWER_PX;
    const clampedReference = Math.max(
        CAROUSEL_SCALE_FLUID_WIDTH_VIEWPORT_MIN_PX,
        Math.min(referenceWidth, CAROUSEL_SCALE_FLUID_WIDTH_VIEWPORT_MAX_PX),
    );
    const progress =
        (clampedReference - CAROUSEL_SCALE_FLUID_WIDTH_VIEWPORT_MIN_PX) / widthSpan;

    return Math.round(
        CAROUSEL_SCALE_FLUID_MIN_WIDTH_LOWER_PX + progress * minWidthSpan,
    );
}

/** Largest equal min width that still fits all choices in the container (within 58–68px). */
export function resolveFluidDesktopMinCellWidthForContainer(
    containerWidth: number,
    choiceCount: number,
    itemMarginPx = CAROUSEL_SCALE_ITEM_MARGIN_PX,
    containerBufferPx = CARROUSEL_SCALE_HORIZONTAL_WRAP_BUFFER_PX,
): number {
    const interpolatedMin = computeFluidDesktopMinCellWidth(containerWidth);

    if (choiceCount <= 0 || containerWidth <= 0) {
        return interpolatedMin;
    }

    const availableWidth = containerWidth - containerBufferPx - choiceCount * itemMarginPx;
    const maxEqualMinThatFits = availableWidth / choiceCount;

    if (maxEqualMinThatFits < CAROUSEL_SCALE_FLUID_MIN_WIDTH_LOWER_PX) {
        return CAROUSEL_SCALE_FLUID_MIN_WIDTH_LOWER_PX;
    }

    return Math.round(
        Math.min(interpolatedMin, maxEqualMinThatFits, CAROUSEL_SCALE_FLUID_MIN_WIDTH_UPPER_PX),
    );
}

export function resolveCarrouselScaleMinCellWidth(
    isMobile: boolean,
    isTablet: boolean,
    referenceWidth: number,
    choiceCount = 0,
): number {
    if (isMobile || isTablet) {
        return CAROUSEL_SCALE_CELL_MIN_WIDTH_MOBILE_PX;
    }

    if (referenceWidth >= CAROUSEL_SCALE_LARGE_DISPLAY_MIN_VIEWPORT_PX) {
        return CAROUSEL_SCALE_CELL_MIN_WIDTH_LARGE_PX;
    }

    return resolveFluidDesktopMinCellWidthForContainer(referenceWidth, choiceCount);
}

function resolveResponsiveCellHeight(isMobile: boolean, isTablet: boolean): number {
    if (isMobile) {
        return CAROUSEL_SCALE_CELL_HEIGHT_MOBILE_PX;
    }
    if (isTablet) {
        return CAROUSEL_SCALE_CELL_HEIGHT_TABLET_PX;
    }
    return CAROUSEL_SCALE_CELL_HEIGHT_PX;
}

function shouldCenterContentRow(
    contentWidths: number[],
    containerWidth: number,
    itemMarginPx: number,
    containerBufferPx: number,
    gapPx: number,
): boolean {
    const totalContentWidth = totalRowWidth(
        contentWidths,
        itemMarginPx,
        containerBufferPx,
        gapPx,
    );
    return totalContentWidth / containerWidth < CAROUSEL_SCALE_CENTER_ROW_FILL_RATIO;
}

export function computeCarrouselScaleLayoutPlan(
    params: ComputeCarrouselScaleLayoutPlanParams,
): CarrouselScaleLayoutPlan {
    const {
        labels,
        containerWidth,
        borderWidthPx,
        font,
        isMobile = false,
        isTablet = false,
        isLargeDisplay = false,
        itemMarginPx = CAROUSEL_SCALE_ITEM_MARGIN_PX,
        containerBufferPx = CARROUSEL_SCALE_HORIZONTAL_WRAP_BUFFER_PX,
        gapPx = 0,
    } = params;

    const cellHeight = resolveResponsiveCellHeight(isMobile, isTablet);
    const minCellWidthReference = isLargeDisplay
        ? CAROUSEL_SCALE_LARGE_DISPLAY_MIN_VIEWPORT_PX
        : containerWidth;
    const minCellWidthPx = resolveCarrouselScaleMinCellWidth(
        isMobile,
        isTablet,
        minCellWidthReference,
        labels.length,
    );
    const paddingSteps = isMobile
        ? CAROUSEL_SCALE_CONTENT_PADDING_X_STEPS_MOBILE
        : CAROUSEL_SCALE_CONTENT_PADDING_X_STEPS;
    const minPaddingX = paddingSteps[paddingSteps.length - 1];

    if (labels.length === 0 || containerWidth <= 0) {
        return {
            useVertical: true,
            contentPaddingX: minPaddingX,
            cellWidths: [],
            cellHeight,
            minCellWidthPx,
            stretchToRow: false,
            centerRow: false,
        };
    }

    const equalCellWidth = computeEqualCellWidth(
        containerWidth,
        labels.length,
        itemMarginPx,
        containerBufferPx,
    );

    if (equalCellWidth < minCellWidthPx) {
        return {
            useVertical: true,
            contentPaddingX: minPaddingX,
            cellWidths: [],
            cellHeight,
            minCellWidthPx,
            stretchToRow: false,
            centerRow: false,
        };
    }

    for (const paddingX of paddingSteps) {
        const contentWidths = labels.map((label) =>
            computeContentCellWidth(label, paddingX, borderWidthPx, font, minCellWidthPx),
        );

        if (horizontalRowFits(contentWidths, containerWidth, itemMarginPx, containerBufferPx, gapPx)) {
            return {
                useVertical: false,
                contentPaddingX: paddingX,
                cellWidths: contentWidths,
                cellHeight,
                minCellWidthPx,
                stretchToRow: false,
                centerRow: shouldCenterContentRow(
                    contentWidths,
                    containerWidth,
                    itemMarginPx,
                    containerBufferPx,
                    gapPx,
                ),
            };
        }

        const equalWidths = labels.map(() => equalCellWidth);
        if (
            horizontalRowFits(equalWidths, containerWidth, itemMarginPx, containerBufferPx, gapPx) &&
            labels.every((label) =>
                labelFitsInCell(label, equalCellWidth, paddingX, borderWidthPx, font),
            )
        ) {
            return {
                useVertical: false,
                contentPaddingX: paddingX,
                cellWidths: equalWidths,
                cellHeight,
                minCellWidthPx,
                stretchToRow: true,
                centerRow: false,
            };
        }
    }

    return {
        useVertical: true,
        contentPaddingX: minPaddingX,
        cellWidths: [],
        cellHeight,
        minCellWidthPx,
        stretchToRow: false,
        centerRow: false,
    };
}
